import { UserInviteLocation } from '@data/users/model/userManagement'

export interface Role {
  id: string
  name: string
  description: string
  permissions: Permission[]
}

export interface Group {
  id: string
  name: string
  description: string
  roles: Role[]
}

export interface Permission {
  id: string
  name: string
  description: string
}

export interface UserInviteAccounts extends UserAccountAssoc {
  invitationIdRef: number
}

export interface UserInvite {
  id: number
  email: string
  active: boolean
  createAt: string
  updatedAt: string
  accounts: UserInviteAccounts[]
  locations?: UserInviteLocation[]
  groupIds?: string[]
  roleIds?: number[]
  permissionIds?: number[]
  welcomeEmail?: boolean
  expiryDate: string
  accountCreated: boolean
}

// `ClientUserRoles` on the API — a closed set on both sides now: requests reject anything
// outside it with 422, and the InDB response models serialize through it too. Order matches
// the enum, and `ClientPositionOptions` derives from this so the dropdown cannot drift from
// the API again. It silently did: the API corrected `Sectary` to `Secretary` and the
// hand-maintained option list kept offering a value the API had started refusing.
export const CLIENT_USER_ROLES = [
  'Finance',
  'Sustainability',
  'Maintenance',
  'Admin',
  'Director',
  'CEO',
  'Facilities',
  'Secretary',
  'Customer Success',
  'Procurement',
  'Health and Safety',
  'Tech',
  'Building manager',
  'Site manager',
  'Mechanical Plumber',
  'Installer',
  'Other',
] as const

export type ClientUserRole = (typeof CLIENT_USER_ROLES)[number]

export interface RawUserPreferences {
  uom: string
}

export interface RawUserAccountAssoc {
  id: number
  admin: boolean
  autoAddLocations: boolean
  autoAddAlerts: boolean
  invoice: boolean
  accountContact: boolean
  position: ClientUserRole
  createdAt?: string | null
  updatedAt?: string | null
}

export interface RawUserLocationType {
  id: number
  userIdRef: number
  customerLocationIdRef: number
  admin: boolean
  emergencyContact: boolean
}

export interface RawUserResponse {
  id: number
  createdAt?: string | null
  updatedAt?: string | null
  email: string
  active: boolean
  emailConfirmedAt?: string | null
  firstName?: string | null
  lastName?: string | null
  mobile?: string | null
  accountIds?: number[] | null
  accounts: RawUserAccountAssoc[]
  adminAccountIds?: number[] | null
  viewAllLocationsAccountIds: number[]
  adminCustomerLocationIds: number[]
  accountContactIds: number[]
  customerLocationIds: number[]
  alertLocationIds: Array<number | null>
  groups?: string[] | null
  permissions?: string[] | null
  roles?: string[] | null
  customerLocation: RawUserLocationType[]
  // `UserPublic.preferences` dropped out of the schema's required set — production
  // still declares it required, the new schema does not. Every consumer already
  // reads it as `userInfo?.preferences?.uom`, so nullable costs them nothing.
  preferences?: RawUserPreferences | null
}

export interface UserAccountAssoc {
  accountId: string
  autoAddLocations: boolean
  admin: boolean
  autoAddAlerts: boolean
  invoice: boolean
  accountContact: boolean
  position: ClientUserRole
}

export default interface User {
  id: string
  firstname: string
  lastname: string
  initials?: string
  email: string
  mobile: string
  active: boolean
  // Group *names*, not ids — `UserPublic.groups` is built from each group's `name`.
  // Anything sending group ids to the API (`groupIds` on create and invite) must take
  // them from `Group.id`, not from here. The invite flow used to overload this field
  // with ids on an `any`-typed object, which is why it worked and read as if it should not.
  groups: string[]
  accountIds: string[]
  adminAccountIds: string[]
  customerLocationIds: string[]
  viewAllLocationsAccountIds: string[]
  adminCustomerLocationIds: string[]
  alertLocationIds: string[]
  customerLocations: { locationId: string; admin: boolean; emergencyContact: boolean }[]
  accounts: UserAccountAssoc[]
  preferences: {
    uom: string
    // lang: string
  } | null
  accountContactIds: string[]
}